import { apiAuthFetch } from "@/lib/authFetch";
import i18n from "@/lib/i18n";

export interface TableData {
  headers: string[];
  rows: string[][];
}

export function extractTableData(table: HTMLTableElement): TableData | null {
  const headers: string[] = [];
  const rows: string[][] = [];

  const thElements = table.querySelectorAll("thead th");
  for (const th of thElements) {
    headers.push((th.textContent ?? "").trim());
  }

  const trElements = table.querySelectorAll("tbody tr");
  for (const tr of trElements) {
    const row: string[] = [];
    const cells = tr.querySelectorAll("td");
    for (const td of cells) {
      row.push((td.textContent ?? "").trim());
    }
    rows.push(row);
  }

  if (headers.length === 0 && rows.length === 0) return null;

  return { headers, rows };
}

function escapeCsvField(field: string): string {
  if (field.includes(",") || field.includes('"') || field.includes("\n") || field.includes("\r")) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

export function downloadCsv(data: TableData, fileName = `table_export_${Date.now()}`): void {
  const lines: string[] = [];

  if (data.headers.length > 0) {
    lines.push(`"${data.headers.map(escapeCsvField).join('","')}"`);
  }

  for (const row of data.rows) {
    lines.push(`"${row.map(escapeCsvField).join('","')}"`);
  }

  const csvContent = `\uFEFF${lines.join("\r\n")}`;
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, fileName.endsWith(".csv") ? fileName : `${fileName}.csv`);
}

export async function downloadExcel(data: TableData, fileName = `table_export_${Date.now()}`): Promise<void> {
  const response = await apiAuthFetch("api/export/excel", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      headers: data.headers,
      rows: data.rows,
      fileName,
    }),
  });

  if (!response.ok) {
    throw new Error(i18n.t("error.code.service_unavailable"));
  }

  const disposition = response.headers.get("Content-Disposition");
  const match = disposition?.match(/filename="?([^";\s]+)"?/);
  const resolvedName = match?.[1] ?? (fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`);

  const blob = await response.blob();
  triggerDownload(blob, resolvedName);
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
